'use client';

import {useState, useEffect} from 'react';
import {useTranslations, useLocale} from 'next-intl';

interface Projet {
  id: number;
  titre_fr: string;
  categorie: string;
  statut: string;
  budget_objectif: number;
  montant_collecte: number;
  gouvernorat?: string;
  urgent: boolean;
}

export default function FeaturedProjects() {
  const locale = useLocale();
  const t = useTranslations('home');
  const [projets, setProjets] = useState<Projet[]>([]);

  useEffect(() => {
    fetch('/api/v1/projets/?limit=6')
      .then(r => r.ok ? r.json() : [])
      .then(data => setProjets(data))
      .catch(() => {});
  }, []);

  if (projets.length === 0) return null;

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary">{t('featured_title')}</h2>
          <p className="text-muted mt-2 text-sm">{t('featured_desc')}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projets.map(p => {
            const pct = p.budget_objectif > 0 ? Math.round((p.montant_collecte / p.budget_objectif) * 100) : 0;
            return (
              <a
                key={p.id}
                href={`/${locale}/projets/${p.id}`}
                style={{textDecorationColor: 'transparent'}}
                className="block rounded-xl border border-border bg-white hover:shadow-lg transition-shadow p-5"
              >
                {p.urgent && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded mb-2 inline-block">{t('urgent_badge')}</span>}
                <h3 className="text-sm font-semibold text-foreground leading-tight mb-1">{p.titre_fr}</h3>
                <div className="text-xs text-muted mb-3 capitalize">{p.categorie}{p.gouvernorat ? ` — ${p.gouvernorat}` : ''}</div>
                <div className="h-1.5 bg-border rounded-full overflow-hidden mb-1">
                  <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-primary' : 'bg-orange-500'}`} style={{width: `${Math.min(pct, 100)}%`}} />
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-muted">{p.montant_collecte?.toLocaleString()} / {p.budget_objectif?.toLocaleString()} TND</span>
                  <span className="font-bold text-primary">{pct}%</span>
                </div>
              </a>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <a href={`/${locale}/projets`} style={{color: '#1B5E20', textDecorationColor: 'transparent'}} className="inline-flex items-center gap-2 border border-primary text-sm font-medium px-5 py-2 rounded-lg hover:bg-primary/5 transition-colors no-underline">
            {t('featured_cta')}
          </a>
        </div>
      </div>
    </section>
  );
}
